import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, X, UsersRound, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Settings() {
  const { user } = useAuth();
  const { refresh: refreshBranding } = useBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user?.role === "super_admin") api.get("/settings/company").then((r) => setForm(r.data)); }, [user]);

  if (user && user.role !== "super_admin") return <Navigate to="/" replace />;

  function onFile(key) {
    return (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const rd = new FileReader(); rd.onload = () => setForm({ ...form, [key]: rd.result }); rd.readAsDataURL(f);
    };
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/company", form);
      setForm(data);
      // Refresh sidebar + login branding instantly (logo / company name / hero)
      refreshBranding();
      toast.success("Settings saved");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  }
  if (!form) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Company identity, branch, bank details, PFI personalisation and login hero image.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5 border border-slate-200 shadow-none space-y-3 rounded-xl">
          <div className="heading font-semibold text-slate-900">Company</div>
          <div><Label>Company Name</Label><Input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="set-company-name" /></div>
          <div><Label>Branch / Factory Name</Label><Input value={form.branch_name || ""} onChange={(e) => setForm({ ...form, branch_name: e.target.value })} placeholder="Hyderabad Industrial Branch" data-testid="set-branch-name" /></div>
          <div><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Telangana" /></div>
            <div><Label>GSTIN</Label><Input value={form.gst || ""} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
            <div><Label>Default GST %</Label><Input type="number" value={form.default_gst_pct ?? 18} onChange={(e) => setForm({ ...form, default_gst_pct: Number(e.target.value) })} /></div>
          </div>
          <div className="text-xs text-slate-500">Same-state distributors → CGST + SGST. Different state → IGST.</div>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none space-y-3 rounded-xl">
          <div className="heading font-semibold text-slate-900">Bank &amp; UPI</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account Holder / Display</Label><Input value={form.upi_name || ""} onChange={(e) => setForm({ ...form, upi_name: e.target.value })} /></div>
            <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
            <div><Label>Account Number</Label><Input value={form.bank_account || ""} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></div>
            <div><Label>IFSC</Label><Input value={form.bank_ifsc || ""} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })} /></div>
          </div>
          <div>
            <Label>UPI QR Image</Label>
            <div className="flex items-start gap-4 mt-1">
              <div className="w-32 h-32 border border-dashed border-slate-300 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                {form.upi_image_data ? <img src={form.upi_image_data} alt="UPI" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">No image</span>}
              </div>
              <div className="flex-1 space-y-2">
                <Input type="file" accept="image/*" onChange={onFile("upi_image_data")} data-testid="upi-image-upload" />
                <div className="text-xs text-slate-500">Upload your fixed UPI QR image. Embedded as-is into every PFI PDF.</div>
                {form.upi_image_data && <Button variant="outline" size="sm" onClick={() => setForm({ ...form, upi_image_data: "" })}><X className="h-3 w-3 mr-1" /> Remove</Button>}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none space-y-3 rounded-xl">
          <div>
            <div className="heading font-semibold text-slate-900">App Logo</div>
            <div className="text-xs text-slate-500 mt-0.5">Shown in the sidebar top-left <b>and</b> on the login page brand tile.</div>
          </div>
          <div className="flex items-start gap-5">
            <div className="w-24 h-24 border border-dashed border-slate-300 rounded-xl bg-slate-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="app-logo-preview">
              {form.app_logo_data
                ? <img src={form.app_logo_data} alt="App logo" className="max-h-full max-w-full object-contain p-2" />
                : <span className="text-[10px] text-slate-400 text-center px-2">No logo<br/>uploaded</span>}
            </div>
            <div className="flex-1 space-y-2">
              <Input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile("app_logo_data")} data-testid="app-logo-upload" />
              <div className="text-xs text-slate-500">PNG / JPG / SVG · <b>1:1 square recommended</b> · transparent PNG looks best on the dark sidebar.</div>
              {form.app_logo_data && <Button variant="outline" size="sm" onClick={() => setForm({ ...form, app_logo_data: "" })} data-testid="app-logo-remove"><X className="h-3 w-3 mr-1" /> Remove</Button>}
            </div>
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none space-y-3 rounded-xl">
          <div>
            <div className="heading font-semibold text-slate-900">PDF Export Logo</div>
            <div className="text-xs text-slate-500 mt-0.5">Embedded in <b>every PDF export</b> — PFI, Production Sheet & Load Sheet. Independent from the app logo.</div>
          </div>
          <div className="flex items-start gap-5">
            <div className="w-40 h-20 border border-dashed border-slate-300 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0" data-testid="pdf-logo-preview">
              {form.logo_data
                ? <img src={form.logo_data} alt="PDF logo" className="max-h-full max-w-full object-contain p-2" />
                : <span className="text-[10px] text-slate-400 text-center px-2">No PDF logo</span>}
            </div>
            <div className="flex-1 space-y-2">
              <Input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile("logo_data")} data-testid="logo-upload" />
              <div className="text-xs text-slate-500">PNG / JPG · Wide/landscape letterhead works well here (e.g. 400×120). Different from the square app logo.</div>
              {form.logo_data && <Button variant="outline" size="sm" onClick={() => setForm({ ...form, logo_data: "" })} data-testid="logo-remove"><X className="h-3 w-3 mr-1" /> Remove</Button>}
            </div>
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none space-y-3 rounded-xl">
          <div>
            <div className="heading font-semibold text-slate-900">Login Hero Image</div>
            <div className="text-xs text-slate-500 mt-0.5">Right-side brand artwork on the sign-in screen. Not a logo.</div>
          </div>
          <div className="flex items-start gap-5">
            <div className="w-40 h-28 border border-dashed border-slate-300 rounded-lg bg-slate-950 flex items-center justify-center overflow-hidden shrink-0" data-testid="hero-preview">
              {form.login_hero_data ? <img src={form.login_hero_data} alt="Hero" className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400">No image</span>}
            </div>
            <div className="flex-1 space-y-2">
              <Input type="file" accept="image/*" onChange={onFile("login_hero_data")} data-testid="hero-upload" />
              <div className="text-xs text-slate-500">Recommended 1600×1200 or larger · dark/moody tones preferred for legibility.</div>
              {form.login_hero_data && <Button variant="outline" size="sm" onClick={() => setForm({ ...form, login_hero_data: "" })} data-testid="hero-remove"><X className="h-3 w-3 mr-1" /> Remove</Button>}
            </div>
          </div>
        </Card>
      </div>

      <Link to="/settings/roles" data-testid="settings-roles-link" className="block">
        <Card className="p-5 border border-slate-200 shadow-none rounded-xl hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                <UsersRound className="w-5 h-5" />
              </div>
              <div>
                <div className="heading font-semibold text-slate-900">Roles &amp; Permissions</div>
                <div className="text-sm text-slate-500 mt-0.5">Manage role templates — modules, actions, and commercial visibility. Clone and customise as needed.</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
          </div>
        </Card>
      </Link>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-settings">
          <Upload className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
